import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${window.location.origin}/api`;

export default function GoogleCompleteProfilePage() {
  const { user, isAuthenticated, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [loading, isAuthenticated, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error('Phone number is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/customer/profile`, { phone }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success('Phone number saved!');
      navigate('/account');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
    </div>
  );

  if (!user) return null;

  return (
    <div data-testid="google-complete-profile-page" className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full mx-auto mb-3" />
          ) : (
            <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-black text-xl font-medium">{(user.name || user.email || '?')[0].toUpperCase()}</span>
            </div>
          )}
          <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Almost There!
          </h1>
          <p className="text-white/40 text-sm mt-1 font-sans">
            Hi {user.name || 'there'}! Just need your phone number for delivery updates.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="relative">
            <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="tel"
              placeholder="Phone Number (e.g. 09123456789)"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full pl-7 bg-transparent border-b border-white/20 text-white py-3 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="google-phone-input"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="google-save-phone-btn"
          >
            {saving ? 'Saving...' : 'Continue'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="w-full text-white/40 text-sm hover:text-white transition-colors py-2"
          >
            Skip for now
          </button>
        </form>
      </div>
    </div>
  );
}
